import { AccountInterface, PaymentMode } from "../interfaces";

/**
 * Returns the draft's accountBillTo when its credit lines and agreement may
 * shape the payment mode options, or undefined when the bill-to is (or was)
 * the consignee.
 *
 * The consignee's credit lines must never be offered as a payment mode.
 * COD and DEST_BOXOFFICE_CREDIT set the draft's accountBillTo to the
 * consignee, and after a consignee swap accountBillTo can still point to the
 * previous consignee, so both the identity and the payment mode are checked:
 * either signal alone leaves a leak (identity misses the stale-consignee
 * sequence, payment mode misses the locker flow that bills the consignee
 * under CREDIT).
 */
export const getCreditBillTo = (
  accountBillTo?: AccountInterface,
  consignee?: AccountInterface,
  paymentMode?: PaymentMode
): AccountInterface | undefined => {
  const billToIsConsignee =
    !!accountBillTo?.id && accountBillTo.id === consignee?.id;
  const billToPaysAtDestination =
    paymentMode === PaymentMode.COD ||
    paymentMode === PaymentMode.DEST_BOXOFFICE_CREDIT;

  return billToIsConsignee || billToPaysAtDestination
    ? undefined
    : accountBillTo;
};

const collectAuthAccounts = (
  shipper?: AccountInterface,
  creditBillTo?: AccountInterface
): AccountInterface[] =>
  (shipper?.listAuthorizingAccount ?? []).concat(
    creditBillTo?.listAuthorizingAccount ?? []
  );

/**
 * Builds the list of authorizing accounts whose credit lines may be offered
 * in the payment mode selector. See getCreditBillTo for why accountBillTo's
 * accounts are sometimes left out.
 */
export const getPaymentModeAuthAccounts = (
  shipper?: AccountInterface,
  accountBillTo?: AccountInterface,
  consignee?: AccountInterface,
  paymentMode?: PaymentMode
): AccountInterface[] =>
  collectAuthAccounts(
    shipper,
    getCreditBillTo(accountBillTo, consignee, paymentMode)
  );

/**
 * Lists the authorizing accounts, accountSites and agreements whose BU
 * restrictions decide whether the selected store is a special-contract store.
 *
 * It takes the result of getCreditBillTo so the restrictions come from the
 * same accounts getPaymentModeAuthAccounts offers. If they came from the raw
 * accountBillTo instead, a store granted by the consignee's agreement would
 * count as special-contract (hiding Contado and COD) while none of the
 * consignee's credit lines is offered, leaving the selector empty.
 */
export const getCorporateRestrictionSources = (
  shipper?: AccountInterface,
  creditBillTo?: AccountInterface
): {
  authAccounts: AccountInterface[];
  accountSiteIDs: number[];
  agreementIDs: string[];
} => {
  const authAccounts = collectAuthAccounts(shipper, creditBillTo);

  const accountSiteIDs = Array.from(
    new Set(
      authAccounts
        .map((account) => account.accountSiteID)
        .filter((accountSiteID): accountSiteID is number =>
          Number.isFinite(accountSiteID)
        )
    )
  );

  const agreementIDs = Array.from(
    new Set(
      [
        shipper?.agreementID,
        creditBillTo?.agreementID,
        ...authAccounts.map((account) => account.agreementID),
      ]
        .map((agreementID) => agreementID?.trim() ?? "")
        .filter((agreementID) => agreementID.length > 0)
    )
  );

  return { authAccounts, accountSiteIDs, agreementIDs };
};
